import {Controller} from '@hotwired/stimulus';

/**
 * Panneau flottant d'un formulaire d'édition, ouvert par un bouton (tuile de réglage, sélecteur d'état, cadre
 * d'image) : le panneau règle des champs du même formulaire, rien ne s'applique avant « Enregistrer ».
 *
 * Un seul panneau ouvert à la fois sur la page. Échap ou un clic au-dehors le ferme ; à l'ouverture le focus va au
 * premier champ (la réponse cochée d'un groupe de radios, jamais une pastille d'aide), à la fermeture il revient au
 * bouton. Le panneau se cale à droite s'il déborderait de la fenêtre.
 * Un lien de la page vers un champ du panneau fermé (récapitulatif d'erreurs) l'ouvre d'abord ; `open` l'ouvre au
 * chargement (champ en erreur).
 *
 *   <div {{ stimulus_controller('setting-popover', {open: false}) }}>
 *     <button type="button" aria-expanded="false" aria-controls="x" {{ stimulus_target('setting-popover', 'trigger') }}
 *             {{ stimulus_action('setting-popover', 'toggle') }}>…</button>
 *     <div id="x" hidden {{ stimulus_target('setting-popover', 'panel') }}>…</div>
 *   </div>
 */
export default class extends Controller {
  static targets = ['trigger', 'panel'];

  static values = {open: Boolean};

  connect() {
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onOtherOpened = this.onOtherOpened.bind(this);
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('setting-popover:opened', this.onOtherOpened);

    // ouvert au chargement pour une erreur : il ne ferme pas les autres panneaux, eux aussi peut-être en erreur
    if (this.openValue) {
      this.open({focus: false, exclusive: false});
    }
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('setting-popover:opened', this.onOtherOpened);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open({focus = true, exclusive = true} = {}) {
    this.panelTarget.hidden = false;
    this.triggerTarget.setAttribute('aria-expanded', 'true');
    this.#place();

    if (exclusive) {
      this.dispatch('opened', {target: window, prefix: 'setting-popover', detail: {controller: this}});
    }

    if (focus) {
      this.#firstField()?.focus();
    }
  }

  close({restoreFocus = true} = {}) {
    if (!this.isOpen) {
      return;
    }

    const hadFocus = this.panelTarget.contains(document.activeElement);

    this.panelTarget.hidden = true;
    this.triggerTarget.setAttribute('aria-expanded', 'false');

    if (restoreFocus && hadFocus) {
      this.triggerTarget.focus();
    }
  }

  escape(event) {
    if (this.isOpen) {
      event.stopPropagation();
      this.close();
    }
  }

  onOtherOpened({detail}) {
    if (detail.controller !== this) {
      this.close({restoreFocus: false});
    }
  }

  onDocumentClick(event) {
    const link = event.target.closest('a[href^="#"]');

    if (link && this.#reveal(link, event)) {
      return;
    }

    // la médiathèque et les listes de combobox s'ouvrent hors du panneau : un clic n'y vaut pas « au-dehors »
    if (this.isOpen && !this.element.contains(event.target) && document.contains(event.target) &&
      !event.target.closest('.modal, .fancybox-container, .fancybox__container')) {
      this.close({restoreFocus: false});
    }
  }

  get isOpen() {
    return !this.panelTarget.hidden;
  }

  #reveal(link, event) {
    if (link.hash.length < 2 || this.isOpen) {
      return false;
    }

    const field = document.getElementById(decodeURIComponent(link.hash.slice(1)));

    if (!field || !this.panelTarget.contains(field)) {
      return false;
    }

    event.preventDefault();
    this.open({focus: false});
    field.scrollIntoView({block: 'center'});
    field.focus({preventScroll: true});

    return true;
  }

  // calé sur le bord gauche du bouton, ou sur son bord droit s'il déborderait de la fenêtre
  #place() {
    this.panelTarget.classList.remove('is-end');

    if (this.panelTarget.getBoundingClientRect().right > document.documentElement.clientWidth) {
      this.panelTarget.classList.add('is-end');
    }
  }

  #firstField() {
    const field = [...this.panelTarget.querySelectorAll('input:not([type=hidden]), select, textarea, button:not(.help-tip-trigger)')]
      .find((candidate) => !candidate.disabled && candidate.offsetParent !== null && !candidate.closest('[hidden]'));

    if (field?.type === 'radio' && field.form !== null) {
      const checked = [...field.form.elements].find((radio) => radio.name === field.name && radio.checked);

      return checked ?? field;
    }

    return field;
  }
}
